import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { AuthGuard } from './core/auth';
import { SigninRedirectCallbackComponent } from './core/auth/oidc-client/signin-redirect-callback.component';
import { SignoutRedirectCallbackComponent } from './core/auth/oidc-client/signout-redirect-callback.component';
import { ForbiddenComponent, GeneralErrorComponent, NotFoundComponent, UnauthorizedComponent } from './core/errors';
import { NavigationRoutes } from './core/navigation';
import { HomeTemporaryGuard } from './home/home-temporary.guard';
import { HomeComponent } from './home/home.component';
import { PublicMemberSearchComponent } from './public-member-search/public-member-search.component';
import { BadBrowserPageComponent } from './shared/bad-browser-page/bad-browser-page.component';
import { BadBrowserGuard } from './shared/bad-browser-page/bad-browser.guard';
import { ConfidentialityAgreementComponent } from './shared/confidentiality-agreement/confidentiality-agreement.component';
import { ContactUsComponent } from './shared/contact-us/contact-us.component';
import { FullComponent } from './shared/layouts/full/full.component';
import { MaintenancePageComponent } from './shared/maintenance/maintenance-page/maintenance-page.component';
import { MaintenanceGuard } from './shared/maintenance/maintenance.guard';
import { SystemRoles } from './shared/security/system-roles';
import { TermsOfUseComponent } from './shared/terms-of-use/terms-of-use.component';
import { SharedProjectsGuard } from './shared-projects/shared-projects.guard';
import { PaymentComponent } from './payment/payment.component';

const routes: Routes = [
  {
    path: '', component: FullComponent, children: [
      {
        path: 'home',
        canActivate: [BadBrowserGuard, HomeTemporaryGuard],
        children: [
          {
            path: '',
            component: HomeComponent
          }],
      },
      {
        path: '',
        redirectTo: 'home',
        pathMatch: 'full'
      },
      {
        path: 'company',
        canActivate: [AuthGuard, BadBrowserGuard, MaintenanceGuard],
        data: {
          auth: {
            roles: [SystemRoles.rolePrincipalRepresentative, SystemRoles.roleExternalRepresentative],
          },
        },
        loadChildren: () => import('./company/company.module').then(m => m.CompanyModule),
      },
      {
        path: 'shopping-cart',
        canActivate: [AuthGuard],
        loadChildren: () => import('./shopping-cart/shopping-cart.module').then(m => m.ShoppingCartModule),
      },
      {
        path: 'continuing-education',
        canActivate: [AuthGuard, MaintenanceGuard],
        loadChildren: () => import('./continuing-education/continuing-education.module').then(m => m.ContinuingEducationModule),
      },
      {
        path: 'student-card',
        canActivate: [AuthGuard, MaintenanceGuard],
        loadChildren: () => import('./student-card/student-card.module').then(m => m.StudentCardModule),
      },
      {
        path: 'member-card',
        canActivate: [AuthGuard, MaintenanceGuard],
        loadChildren: () => import('./member-card/member-card.module').then(m => m.MemberCardModule),
      },
      {
        path: 'addresses',
        canActivate: [AuthGuard, MaintenanceGuard],
        loadChildren: () => import('./addresses/addresses.module').then(m => m.AddressesModule),
      },
      {
        path: 'inscription',
        canActivate: [AuthGuard, MaintenanceGuard],
        loadChildren: () => import('./inscription/inscription.module').then(m => m.InscriptionModule),
      },
      {
        path: 'bills-invoices',
        canActivate: [AuthGuard],
        children: [
          {
            path: '',
            loadChildren: () => import('./billsInvoices/bills/bills.module').then(m => m.BillsModule),
          },
          {
            path: 'bills',
            loadChildren: () => import('./billsInvoices/bills/bills.module').then(m => m.BillsModule),
          },
          {
            path: 'invoices',
            loadChildren: () => import('./billsInvoices/invoices/invoices.module').then(m => m.InvoicesModule),
          }
        ]
      },
      {
        path: 'farp-subscription',
        canActivate: [AuthGuard],
        loadChildren: () => import('./farp-odq/subscription-farpodq.module').then(m => m.SubscriptionFarpOdqModule),
      },
      {
        path: 'professional-services',
        canActivate: [AuthGuard],
        loadChildren: () => import('./inspection/inspection.module').then(m => m.InspectionModule),
      },
      {
        path: 'dental-assistant',
        canActivate: [AuthGuard],
        loadChildren: () => import('./dental-assistant/dental-assistant.module').then(m => m.DentalAssistantModule),
      }, 
      {
        path: 'shared-projects',
        canActivate: [AuthGuard, SharedProjectsGuard],
        loadChildren: () => import('./shared-projects/shared-projects.module').then(m => m.SharedProjectsModule),
      },
      {
        path: 'messages',
        canActivate: [AuthGuard],
        loadChildren: () => import('./messages/messages.module').then(m => m.MessagesModule),
      },
      {
        path: 'payment',
        component: PaymentComponent
      },
      {
        path: 'student-payment',
        redirectTo: 'payment',
        pathMatch: 'full'
      },
      {
        path: NavigationRoutes.TermsOfUseRoute,
        component: TermsOfUseComponent,
      },
      {
        path: NavigationRoutes.ConfidentialityAgreementRoute,
        component: ConfidentialityAgreementComponent,
      },
      {
        path: NavigationRoutes.ContactUsRoute,
        component: ContactUsComponent,
      },
      {
        path: NavigationRoutes.MaintenanceRoute,
        component: MaintenancePageComponent,
      },
      {
        path: NavigationRoutes.BadBrowserRoute,
        component: BadBrowserPageComponent,
      },
      { path: 'oidc-signin-callback', component: SigninRedirectCallbackComponent },
      { path: 'oidc-signout-callback', component: SignoutRedirectCallbackComponent },
      { path: NavigationRoutes.GeneralErrorRoute, component: GeneralErrorComponent },
      { path: NavigationRoutes.ForbiddenRoute, component: ForbiddenComponent },
      { path: NavigationRoutes.UnauthorizedRoute, component: UnauthorizedComponent },
      { path: NavigationRoutes.NotFoundRoute, component: NotFoundComponent }
    ],
  },
  {
    path: 'bottin',
    component: PublicMemberSearchComponent,
  },
  { path: '**', redirectTo: NavigationRoutes.NotFoundRoute },
];

@NgModule({
  imports: [RouterModule.forRoot(routes, {})],
  exports: [RouterModule],
})
export class AppRoutingModule { }
